import {
  ADS_KEYS_TEXT,
  ADS_OBJECT_TYPES,
} from "../../../../../utils/constants";
import { getUniqueID } from "../../../../../utils/numbers";

/**
 * Resolves the form field path and section IDs for a log change.
 * For ads that don't have adGroupID in metadata, adGroupID is resolved from the campaign form.
 * @param {{ objectType: string, metadata?: object, platform?: string }} params
 * @param {string} field - The field name
 * @param {object} campaign - Current campaign from getValues()
 * @returns {{ path: string, found: boolean, ids?: { platform?: string, adGroupID?: string, adID?: string, assetsGroupID?: string } }}
 */
function resolveFieldPath(
  { objectType, metadata = {}, platform },
  field,
  campaign
) {
  if (objectType === ADS_OBJECT_TYPES.campaign) {
    if (!platform) return { path: "", found: false };

    if (field === "status") {
      return {
        path: `states.${platform}`,
        found: true,
        ids: {
          platform,
          adGroupID: undefined,
          adID: undefined,
          assetsGroupID: undefined,
        },
      };
    } else {
      const platformData = campaign?.platformsData?.[platform];
      if (
        platformData &&
        Object.prototype.hasOwnProperty.call(platformData, field)
      ) {
        return {
          path: `platformsData.${platform}.${field}`,
          found: true,
          ids: {
            platform,
            adGroupID: undefined,
            adID: undefined,
            assetsGroupID: undefined,
          },
        };
      }
    }

    return { path: "", found: false };
  }

  if (objectType === ADS_OBJECT_TYPES.adGroup) {
    const adGroupID = metadata.adGroupID;
    if (!adGroupID) return { path: "", found: false };
    const adsGroups = campaign?.adsGroups || [];
    const idx = adsGroups.findIndex((ag) => ag?.id === adGroupID);
    if (idx < 0) return { path: "", found: false };
    const resolvedId = adsGroups[idx]?.id;
    return {
      path: `adsGroups.[${idx}].${field}`,
      found: true,
      ids: {
        platform,
        adGroupID: resolvedId,
        adID: undefined,
        assetsGroupID: undefined,
      },
    };
  }

  if (objectType === ADS_OBJECT_TYPES.ad) {
    let adGroupID = metadata.adGroupID;
    const adID = metadata.adID;

    if (!adGroupID && !adID) return { path: "", found: false };

    if (!adGroupID && adID) {
      const adsGroups = campaign?.adsGroups || [];
      adsGroups.forEach((ag) => {
        const ads = ag?.ads || [];
        ads.forEach((a) => {
          if (a?.id === adID) {
            adGroupID = a?.adGroupID;
          }
        });
      });
    }

    if (!adGroupID) return { path: "", found: false };

    const adsGroups = campaign?.adsGroups || [];
    const idxG = adsGroups.findIndex((ag) => ag?.id === adGroupID);
    if (idxG < 0) return { path: "", found: false };
    const ads = adsGroups[idxG]?.ads || [];
    const idxA = ads.findIndex((a) => a?.id === adID);
    if (idxA < 0) return { path: "", found: false };
    const resolvedAdGroupID = adsGroups[idxG]?.id;
    const resolvedAdID = ads[idxA]?.id;
    return {
      path: `adsGroups.[${idxG}].ads.[${idxA}].${field}`,
      found: true,
      ids: {
        platform,
        adGroupID: resolvedAdGroupID,
        adID: resolvedAdID,
        assetsGroupID: undefined,
      },
    };
  }

  if (objectType === ADS_OBJECT_TYPES.assetsGroup) {
    const assetsGroupID = metadata.assetsGroupID;
    if (!assetsGroupID) return { path: "", found: false };
    const assetsGroups = campaign?.assetsGroups || [];
    const idx = assetsGroups.findIndex((ag) => ag?.id === assetsGroupID);
    if (idx < 0) return { path: "", found: false };
    const resolvedId = assetsGroups[idx]?.id;
    return {
      path: `assetsGroups.[${idx}].${field}`,
      found: true,
      ids: {
        platform,
        adGroupID: undefined,
        adID: undefined,
        assetsGroupID: resolvedId,
      },
    };
  }

  return { path: "", found: false };
}

/**
 * Verifies if a log change can be reversed by checking that
 * its target path exists in the current campaign form data.
 *
 * Intended to be used in the UI to enable/disable the "reverse" button.
 *
 * @param {{
 *   change: object,
 *   objectType?: string,
 *   platform?: string,
 *   getValues: function
 * }} params
 * @returns {boolean} true if the target exists and can be addressed
 */
export function canReverseLogChange({
  change,
  objectType,
  platform,
  getValues,
}) {
  if (!getValues) return false;

  const campaign = getValues();
  const metadata = change?.metadata || {};
  const field = change?.field;

  if (!field) return false;

  const { path, found } = resolveFieldPath(
    { objectType: objectType || change.objectType, metadata, platform },
    field,
    campaign
  );

  return Boolean(found && path);
}

/**
 * Compares two items for equality. Uses `id` when both have it (robust for interests, locations).
 * Falls back to JSON.stringify for primitives or when id is not available.
 * @param {*} item - Item from form
 * @param {*} target - Item from log change (added/removed)
 * @param {string} field - The field name
 * @returns {boolean}
 */
function itemsMatch(item, target, field) {
  if (item === target) return true;
  if (typeof item !== "object" || item === null || target === null) {
    return String(item) === String(target);
  }
  if (ADS_KEYS_TEXT.includes(field)) {
    return item?.value === target;
  } else {
    // Match by id when both have it (interests, locations, etc.)
    const itemId = item?.id;
    const targetId = target?.id;
    if (itemId != null && targetId != null) {
      return String(itemId) === String(targetId);
    }
    // Fallback: JSON.stringify (may fail if key order differs)
    return JSON.stringify(item) === JSON.stringify(target);
  }
}
const getItemValueFormatted = (value, field) => {
  if (ADS_KEYS_TEXT.includes(field)) {
    return {
      value,
      id: getUniqueID(),
    };
  } else {
    return value;
  }
};

/**
 * Applies the reverse of a log change to the campaign form.
 * @param {{
 *   change: object,
 *   objectType: string,
 *   platform: string,
 *   getValues: function,
 *   setValue: function
 * }} params
 * @returns {{ ok: boolean, error?: string }}
 */
export function applyReverseLogChange({
  change,
  objectType,
  platform,
  getValues,
  setValue,
}) {
  if (!getValues || !setValue)
    return { ok: false, error: "Missing form methods" };

  const campaign = getValues();
  const metadata = change.metadata || {};
  const field = change.field;

  if (!field) return { ok: false, error: "Missing field" };

  const { path, found, ids } = resolveFieldPath(
    { objectType, metadata, platform },
    field,
    campaign
  );

  if (!found || !path) {
    console.log({ found, path });
    return { ok: false, error: "Target entity not found" };
  }

  try {
    if (change.typeChange === "multiple" && (change.added || change.removed)) {
      const currentValue = getValues(path);
      if (!Array.isArray(currentValue)) {
        return { ok: false, error: "Expected array field" };
      }
      let newArray = [...currentValue];
      // Reverse "added": remove those items from the array
      if (change.added && change.added.length > 0) {
        newArray = newArray.filter(
          (item) => !change.added.some((a) => itemsMatch(item, a, field))
        );
      }
      // Reverse "removed": re-add those items to the array
      if (change.removed && change.removed.length > 0) {
        change.removed.forEach((item) => {
          const alreadyExists = newArray.some((x) =>
            itemsMatch(x, item, field)
          );
          if (!alreadyExists) {
            newArray.push(getItemValueFormatted(item, field));
          }
        });
      }
      setValue(path, newArray, { shouldDirty: true });
    } else {
      const valueToSet = change.oldValue;
      setValue(path, valueToSet, { shouldDirty: true });
    }
    return { ok: true, path, ids };
  } catch (err) {
    return { ok: false, error: err?.message || "Unknown error" };
  }
}
